import { useEffect, useState, useRef } from "react";
import { fmtDate } from "@/components/DatePicker";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/RoleBadge";
import { StatCard } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Clock, Play, Square, Timer } from "lucide-react";
import { toast } from "sonner";
import { readQueue, queuePunch, syncQueue, newClientId } from "@/lib/punchQueue";
import { WifiOff } from "lucide-react";

function fmtDur(ms) {
  const s = Math.floor(ms / 1000);
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
}

export default function Timesheet() {
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [queue, setQueue] = useState(readQueue());
  const [online, setOnline] = useState(navigator.onLine);
  const timer = useRef(null);
  useEffect(() => {
    const up = async () => { setOnline(true); const r = await syncQueue(); setQueue(readQueue()); if (r.synced) load(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down); up();
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    api.get("/timesheets/active").then((r) => setActive(r.data)).catch(() => {});
    api.get("/timesheets").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (active) { timer.current = setInterval(() => setNow(Date.now()), 1000); }
    return () => clearInterval(timer.current);
  }, [active]);

  const offlinePunch = (kind, body) => {
    const q = queuePunch(body); setQueue(q);
    if (kind === "in") setActive({ clock_in: body.at, offline: true }); else setActive(null);
    toast.message(`No signal — clock ${kind} saved on this device`, { description: `Recorded ${new Date(body.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}. It syncs automatically when you're back online.` });
  };
  const clockIn = async () => {
    const body = { at: new Date().toISOString(), client_id: newClientId() };
    if (!navigator.onLine || queue.length) return offlinePunch("in", body);
    try { const { data } = await api.post("/timesheets/clock-in"); setActive(data); toast.success("Clocked in"); }
    catch (e) { if (!e.response) offlinePunch("in", body); else toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const clockOut = async () => {
    const body = { at: new Date().toISOString(), client_id: newClientId() };
    if (!navigator.onLine || queue.length) return offlinePunch("out", body);
    try { await api.post("/timesheets/clock-out"); setActive(null); toast.success("Clocked out"); load(); }
    catch (e) { if (!e.response) offlinePunch("out", body); else toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const elapsed = active ? now - new Date(active.clock_in).getTime() : 0;
  const weekHours = history.slice(0, 5).reduce((a, t) => a + (t.hours || 0), 0);
  const otHours = history.reduce((a, t) => a + (t.overtime_hours || 0), 0);

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Timesheet</h1>
        <p className="text-sm text-muted-foreground mt-1">Clock in and track your hours.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card data-testid="timesheet-clock-card" className="lg:col-span-1 p-6 flex flex-col items-center justify-center text-center bg-gradient-to-br from-primary/5 to-blue-500/5">
          <span className={`grid place-items-center w-14 h-14 rounded-2xl mb-4 ${active ? "bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60" : "bg-primary/10 text-primary"}`}>
            <Clock className="w-7 h-7" />
          </span>
          <div className="font-mono-num text-4xl font-bold tracking-tight">{active ? fmtDur(elapsed) : "00:00:00"}</div>
          <div className="text-sm text-muted-foreground mt-1">{active ? (active.offline ? "Clocked in (saved offline)" : "Currently clocked in") : "Not clocked in"}</div>
          {(!online || queue.length > 0) && <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-center gap-2 text-left" data-testid="punch-offline-badge"><WifiOff className="w-3.5 h-3.5 shrink-0" /> {!online ? "You're offline — punches are saved on this device and sync when you reconnect." : ""}{queue.length > 0 && <span data-testid="punch-queue-count">{queue.length} punch{queue.length === 1 ? "" : "es"} waiting to sync</span>}</div>}
          {active ? (
            <Button data-testid="clock-out-btn" variant="destructive" className="mt-5 w-full" onClick={clockOut}><Square className="w-4 h-4 mr-1.5" /> Clock Out</Button>
          ) : (
            <Button data-testid="clock-in-btn" className="mt-5 w-full" onClick={clockIn}><Play className="w-4 h-4 mr-1.5" /> Clock In</Button>
          )}
        </Card>

        <div className="lg:col-span-2 grid grid-cols-2 gap-4 content-start">
          <StatCard label="Hours This Week" value={weekHours.toFixed(1)} icon={Timer} sub="Last 5 shifts" />
          <StatCard label="Overtime Logged" value={otHours.toFixed(1)} icon={Clock} accent="text-amber-600" sub="Hours over 8/day" />
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="p-5 border-b border-border"><h3 className="font-semibold font-display">Weekly Summary</h3></div>
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Overtime</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {history.map((t) => (
              <TableRow key={t.id} data-testid={`timesheet-row-${t.id}`}>
                <TableCell className="font-mono-num">{fmtDate(t.date)}</TableCell>
                <TableCell className="font-mono-num">{t.clock_in ? new Date(t.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                <TableCell className="font-mono-num">{t.clock_out ? new Date(t.clock_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                <TableCell className="text-right font-mono-num">{t.hours?.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono-num text-amber-600">{t.overtime_hours?.toFixed(2)}</TableCell>
                <TableCell><StatusBadge status={t.status} /></TableCell>
              </TableRow>
            ))}
            {history.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No timesheet entries yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
